import { CRAYO_ENTRY, crayoPlan, formatHours, money, crayoPlanTerms } from "@/lib/compare/crayo";
import type { CompareContent } from "@/lib/compare/types";

const hobby = CRAYO_ENTRY;
const clipper = crayoPlan("Clipper");
const pro = crayoPlan("Pro");
const count = (value: number): string => value.toLocaleString("en-US");

const PRICING = "https://clippie.ai/pricing";
const REFUNDS = "https://clippie.ai/refund-policy";
const VS_POST = "https://clippie.ai/blog/clippie-ai-vs-crayo-ai-the-best-tool-for-faceless-viral-video-creation-in";
const VS_POST_DATE = "2025-05-09";
const ALT_POST = "https://clippie.ai/blog/best-crayo-ai-alternative-2026";
const ALT_POST_DATE = "2026-07-13";

export const CLIPPIE_CONTENT: CompareContent = {
  slug: "crayo-vs-clippie",
  competitorSlug: "clippie",
  title: "Crayo vs Clippie AI",
  seoTitle: "Crayo vs Clippie AI: Clipping Editor vs Faceless Templates, Priced",
  seoDescription:
    "Crayo vs Clippie AI, dated prices. Clippie AI Lite is $19.99 for 30 export minutes on six meters and no clipping despite the name. Crayo clips long video into ranked shorts with an editor on every plan.",
  cardVerdict:
    "Better than Crayo for novelty templates and custom voices, behind on clipping, editing and export minutes.",
  bestFor: "Clippers checking whether Clippie AI actually clips",
  datePublished: "2026-10-09",
  dateModified: "2026-10-25",
  verdict:
    `As of October 25, 2026, Crayo is the better pick if you clip real footage: AutoClip ranks the moments in a podcast or stream, reframes faces and opens every clip in a timeline editor on one ${money(hobby.monthly)} plan with ${formatHours(hobby.exportMinutes)} of export. Clippie AI does not clip despite the name; pick it for Would You Rather, Top 5, Quiz and Roblox Rant templates, custom voice slots on the cheapest plan, or Seedance, Veo and Flux generations.`,
  pickCrayoIf: [
    "You clip podcasts, streams or interviews: Crayo's AutoClip ranks moments from sources up to 3 hours and cuts 1 to 20 clips with a face-aware camera, and Clippie AI has no clipping, ranking or reframing despite the name.",
    `You keep a few clips from each long source and want to pay per clip: one Crayo credit is one finished clip, and Hobby carries ${hobby.credits} credits and ${formatHours(hobby.exportMinutes)} of export for ${money(hobby.monthly)}, while Clippie AI Lite caps export at 30 minutes for $19.99 across six meters.`,
    "You want to edit the clip on a timeline with camera track, keyframes, 32 caption styles in 56 spoken languages and a public API on every plan; Clippie AI edits inside each template only.",
  ],
  pickThemIf: [
    "You build a channel on Would You Rather, Top 5, Twitter, Quiz or Roblox Rant videos. Clippie AI ships all five as templates; Crayo offers none of them.",
    "You want your own voice on the entry plan. Clippie AI Lite includes 1 custom voice slot, Creator 10 and Pro 30, with voices importable from ElevenLabs; Crayo has hundreds of stock voices but no cloning.",
    "You want generative video B-roll inside the tool. Clippie AI's AI Studio runs Seedance, Veo and Flux on plan credits; Crayo generates AI images, not AI video.",
  ],
  wins: [
    {
      claim: "Crayo clips long video into ranked shorts; Clippie AI does not, despite the name.",
      evidence:
        "Clippie AI's Split Videos page asks you to supply both the main and the background clip, and Top 5 Moments is a manual 2 to 9 clip countdown; no product page mentions moment detection or reframing as of Oct 8, 2026. Crayo's AutoClip takes a YouTube or TikTok link or a file up to 3 hours, ranks the moments, and cuts 1 to 20 face-framed clips per run.",
      href: "https://clippie.ai/generate/split-video",
    },
    {
      claim: "Crayo includes a full timeline editor on every plan; Clippie AI edits per template.",
      evidence:
        "Clippie AI's templates expose Edit and Arrange & Text tabs and no timeline, per its product pages checked Oct 8, 2026. Crayo's editor has a camera track, keyframes, zooms, transitions, speed ramps, looks, 32 caption styles and keyword highlight on Hobby and up, and every AutoClip clip opens in it.",
      href: "https://crayo.ai/features/auto-clip",
    },
    {
      claim: "Crayo meters one credit per finished clip; Clippie AI runs six meters on every plan.",
      evidence:
        `Clippie AI Lite at $19.99 a month carries 300 AI Studio credits, 30 export minutes, 30 voice minutes, 30 subtitle minutes, 100 AI images and 1 custom voice, per its pricing page on Oct 8, 2026. Crayo Hobby at ${money(hobby.monthly)} a month carries ${hobby.credits} workflow credits, each one a finished clip, plus ${formatHours(hobby.exportMinutes)} of export, ${hobby.voiceoverMinutes} voiceover minutes and ${hobby.images} AI images.`,
      href: PRICING,
    },
    {
      claim: "Crayo publishes a 34-endpoint API and View Tracker; Clippie AI publishes no API, scheduler or analytics.",
      evidence:
        "No API, scheduling or analytics feature appears on clippie.ai or its pricing page as of Oct 8, 2026. Crayo's API covers autoclip, exports, voiceover, images and webhooks on every plan, documented at crayo.ai/developers, and View Tracker follows Instagram, YouTube and TikTok creators.",
      href: "https://crayo.ai/developers",
    },
    {
      claim: "Crayo publishes an annual price; Clippie AI's annual toggle shows no figure.",
      evidence:
        `Clippie AI's pricing page carries a toggle labelled 25% off for annual billing but no annual price rendered on Oct 8, 2026, and every plan says it renews monthly until cancelled. Crayo Hobby is ${money(hobby.monthly)} a month or ${money(hobby.yearlyTotal)} a year, about ${money(hobby.yearlyPerMonth)} a month, both printed on the pricing page.`,
      href: PRICING,
    },
  ],
  concessions: [
    {
      claim: "Clippie AI ships more novelty faceless templates than Crayo.",
      evidence:
        "Its homepage lists Would You Rather, Top 5 Moments, Twitter Videos, Quiz and Roblox Rant alongside Fake Text, Reddit and AI Story, checked Oct 8, 2026. Crayo has none of the five novelty formats; the story and text generators included beside AutoClip on every plan are the limit of its templates.",
      href: "https://clippie.ai",
    },
    {
      claim: "Clippie AI includes custom voice slots on every plan, including Lite.",
      evidence:
        "The pricing page lists 1, 10 and 30 custom voices on Lite, Creator and Pro, and the Voice Studio page says you can import voices from ElevenLabs into your Clippie AI account. Crayo offers hundreds of stock voices and a voice changer but no custom or cloned voices.",
      href: "https://clippie.ai/voice-studio",
    },
    {
      claim: "Clippie AI's AI Studio generates video with Seedance, Veo and Flux.",
      evidence:
        "The homepage describes an AI Studio using Seedance, Veo, Flux and more, metered by AI Studio credits (300 on Lite, 1,000 on Creator, 2,000 on Pro). Crayo generates AI images for scenes and B-roll but does not generate AI video.",
      href: "https://clippie.ai",
    },
    {
      claim: "Clippie AI claims captions in 102 languages; Crayo transcribes 56.",
      evidence:
        "Every Clippie AI plan lists captions in 102 languages on the pricing page checked Oct 8, 2026. Crayo's spoken-language picker has 56 entries. Neither count has been tested for accuracy yet.",
      href: PRICING,
    },
  ],
  typicalMonth: {
    scenario: "One 60-minute source a week, eight clips each: 240 source minutes and 32 clips a month.",
    crayo: {
      plan: "Hobby",
      cost: `${money(hobby.monthly)}/mo or about ${money(hobby.yearlyPerMonth)}/mo yearly`,
      usage: `32 of ${hobby.credits} workflow credits and about 32 of ${hobby.exportMinutes} export minutes`,
      note: "Prices from the live plan table. You post the 32 clips yourself.",
    },
    them: {
      plan: "No equivalent",
      cost: "n/a",
      usage:
        "Clippie AI has no long-to-short clipping: Split Videos needs both the main and the background clip supplied and Top 5 Moments is a manual 2 to 9 clip countdown. Even for clips cut elsewhere, Lite's 30 export minutes at $19.99 would stop at the thirtieth one-minute clip; Creator at $34.99 has 120.",
      note: "Meters and prices from the Clippie AI pricing page, Oct 8, 2026.",
    },
  },
  test: {
    status: "scheduled",
    source:
      "A 20-minute single-speaker talking-head video owned by Crayo, with the same written fake text script given to both tools",
    plans: "Clippie AI Lite and Crayo Clipper, same file where accepted, same day, defaults first then one tuned run each",
  },
  billingNote:
    "Clippie AI bills monthly only through Whop, with an annual toggle that showed no figure on Oct 8, 2026, and every plan states all sales final. Its refund policy says access continues to the end of the paid period after cancellation and that failed generations are refunded as credits, not cash; credit rollover is not stated.",
  corrections: [
    {
      site: "Clippie AI",
      url: VS_POST,
      date: VS_POST_DATE,
      claim: "Crayo lacks the ability to create iMessage-style or WhatsApp-style fake text message story videos.",
      reality:
        "Crayo makes iMessage-style fake text videos on every plan, with AI voiceover from hundreds of voices, and the feature has its own page at crayo.ai/features/fake-texts.",
      evidenceHref: "https://crayo.ai/features/fake-texts",
      evidenceLabel: "Fake text videos",
    },
    {
      site: "Clippie AI",
      url: VS_POST,
      date: VS_POST_DATE,
      claim: "Users cannot fully control layout design, video pacing, or advanced formatting.",
      reality:
        "Every Crayo plan includes a timeline editor with a camera track, keyframes, zooms, transitions, speed ramps, animations, looks, motion blur, fades, 32 caption styles and keyword highlight, and AutoClip offers four layouts, three aspect ratios and ten switchable layers.",
      evidenceHref: "https://crayo.ai/features/auto-clip",
      evidenceLabel: "AutoClip and the editor",
    },
    {
      site: "Clippie AI",
      url: VS_POST,
      date: VS_POST_DATE,
      claim: "The Would You Rather and Top 5 formats are not offered in Crayo's platform.",
      reality:
        "Correct and still current as of October 25, 2026. Crayo has no Would You Rather, Top 5, Quiz, Twitter or Roblox Rant template. AutoClip, Clip Cutter and the timeline editor are the product, with story, text and voiceover generators included beside them.",
      evidenceHref: "https://crayo.ai/pricing",
      evidenceLabel: "Crayo plans",
    },
    {
      site: "Clippie AI",
      url: ALT_POST,
      date: ALT_POST_DATE,
      claim: "Crayo meters usage in workflow credits: 50 per month on the entry plan, 150 and 250 above.",
      reality: `Stale. As of October 25, 2026, Crayo Hobby includes ${hobby.credits} workflow credits a month, Clipper ${count(clipper.credits)} and Pro ${count(pro.credits)}, per the live pricing page. One credit is one finished clip.`,
      evidenceHref: "https://crayo.ai/pricing",
      evidenceLabel: "Crayo pricing",
    },
    {
      site: "Clippie AI",
      url: ALT_POST,
      date: ALT_POST_DATE,
      claim: "Export is limited to 40 minutes, 2 hours and 4 hours by plan.",
      reality: `Stale. Crayo Hobby includes ${formatHours(hobby.exportMinutes)} of export, Clipper ${formatHours(clipper.exportMinutes)} and Pro ${formatHours(pro.exportMinutes)}, per the live pricing page on October 25, 2026.`,
      evidenceHref: "https://crayo.ai/pricing",
      evidenceLabel: "Crayo pricing",
    },
    {
      site: "Clippie AI",
      url: ALT_POST,
      date: ALT_POST_DATE,
      claim: "As of 2026, Crayo offers no free tier and no free trial.",
      reality:
        "Correct. Crayo has no free plan and no trial; twelve free browser tools at crayo.ai/features/free need no account. Clippie AI's own refund policy, section 1.4, states there is no free plan on its side either.",
      evidenceHref: "https://crayo.ai/features/free",
      evidenceLabel: "Free browser tools",
    },
    {
      site: "Clippie AI",
      url: ALT_POST,
      date: ALT_POST_DATE,
      claim: "Trustpilot reviewers have reported being charged after cancelling.",
      reality:
        "Cancelling Crayo is one click in Settings. The plan stays active to the end of the paid cycle and nothing further is charged. The refund policy states the terms in full.",
      evidenceHref: "https://crayo.ai/refund-policy",
      evidenceLabel: "Crayo refund policy",
    },
  ],
  faqs: [
    {
      question: "Is Crayo cheaper than Clippie AI for clipping?",
      answer:
        `For clipping there is nothing to price against: Clippie AI, often written just Clippie, does not clip despite the name. On the meters it publishes, Lite is $19.99 a month for 30 export minutes, 30 voice minutes and 100 AI images, and the next step is Creator at $34.99 with 120 of each minute. Crayo Hobby is ${crayoPlanTerms(hobby)} for ${hobby.credits} workflow credits, one per finished clip, ${formatHours(hobby.exportMinutes)} of export, ${hobby.voiceoverMinutes} voiceover minutes and ${hobby.images} AI images, so Lite is the lower sticker on monthly billing while Crayo's quarterly and yearly prices sit under it. Clippie AI's own July 2026 post concedes that Crayo's annual Hobby price is lower than Lite, which still holds. If you need custom voices or its novelty templates, Clippie AI is the only one of the two that sells them.`,
    },
    {
      question: "Can Clippie AI clip long videos into shorts like Crayo?",
      answer:
        "No. Despite the name, Clippie AI has no long-to-short clipping, moment ranking or face-aware reframing as of Oct 8, 2026. Its Split Videos template is a top/bottom or left/right layout where you upload both the main clip and the background clip yourself, and Top 5 Moments is a manual countdown of 2 to 9 clips you pick. Crayo's AutoClip takes a YouTube or TikTok link or a file from 1 minute to 3 hours, ranks the moments, and cuts 1 to 20 clips of 15 to 120 seconds with a face-aware camera, captions and four layouts.",
    },
    {
      question: "Does Clippie AI have a free plan or trial?",
      answer:
        `No. Clippie AI's refund policy, section 1.4, says there is no free plan, and that a trial exists only where a checkout page states one, converts automatically if not cancelled, and is not refundable once it converts. Crayo has no free plan and no trial either; the entry plan is Hobby at ${money(hobby.monthly)} a month, and twelve free browser tools at crayo.ai/features/free need no account and never upload the file. Both companies state all sales final, so on either side the first month is the trial.`,
    },
    {
      question: "How do I cancel Clippie AI, and does Crayo refund?",
      answer:
        "Clippie AI's pricing page says every plan renews monthly until cancelled and its refund policy says access continues to the end of the paid period after you cancel, but neither page describes where the cancel control lives; billing runs through Whop. Refunds are limited to duplicate charges, renewals after a cancellation, mispriced or unauthorised charges, and failed generations are refunded as credits rather than cash. Crayo does not refund either: all sales are final. Cancelling Crayo is one click in Settings, the plan stays active to the end of the cycle, and nothing further is charged.",
    },
    {
      question: "How many caption languages do Crayo and Clippie AI support?",
      answer:
        "Clippie AI lists captions in 102 languages on every plan, per its pricing page checked Oct 8, 2026, and its blog cites 32 voice languages. Crayo transcribes 56 spoken languages for captions with the same model across all of them, and offers 32 caption styles with keyword highlight in its editor. The language counts have not been tested side by side yet; the scheduled hands-on test will record caption errors on a fixed 60-second segment for both tools.",
    },
    {
      question: "Does Crayo have Would You Rather, Top 5 or Quiz videos?",
      answer:
        "No. Clippie AI is right on this point: Crayo has no Would You Rather, Top 5, Twitter, Quiz or Roblox Rant template as of October 25, 2026. If one of those formats is the channel, Clippie AI wins that row outright. Crayo's product is AutoClip, Clip Cutter and the timeline editor for real footage, with story, text and voiceover generators included on the same plan; if the channel mixes clipped footage with those formats, Crayo covers both and Clippie AI does not clip.",
    },
    {
      question: "Does Crayo also make text message videos? Clippie AI says it does not.",
      answer:
        "Crayo does, as a side feature on every plan rather than the headline. iMessage-style fake text videos with AI voiceover from hundreds of voices have their own page at crayo.ai/features/fake-texts. Clippie AI's May 2025 comparison post states that Crayo lacks fake text message videos, which was not accurate when checked on October 25, 2026. Clippie AI also sells a Fake Text Videos template, so both tools make the format; the difference is that one Crayo credit is one finished video from the same pool as a clip, while Clippie AI counts export, voice and subtitle minutes separately.",
    },
    {
      question: "Is what Clippie AI says about Crayo's pricing accurate?",
      answer:
        `Partly. The July 2026 post quoted the annual prices that were correct until October 2026 and conceded Hobby is cheaper than Lite on that basis. As of October 25, 2026 Hobby is ${crayoPlanTerms(hobby)}, so its yearly price is still under Lite's $19.99 a month while its monthly price is above it. Its credit and export figures are stale: it lists 50, 150 and 250 workflow credits and 40 minutes, 2 hours and 4 hours of export, while the live pricing page on October 25, 2026 shows ${hobby.credits}, ${count(clipper.credits)} and ${count(pro.credits)} credits and ${formatHours(hobby.exportMinutes)}, ${formatHours(clipper.exportMinutes)} and ${formatHours(pro.exportMinutes)} of export. Its no free tier line is correct, and so is its own refund policy's statement that Clippie AI has no free plan. The corrections box above answers each claim with the current figure and a link.`,
    },
  ],
  changelog: [
    {
      date: "2026-10-09",
      text: "First published. Clippie AI prices checked Oct 8, 2026: Lite $19.99/mo with 30 export minutes, Creator $34.99/mo with 120, Pro $69.99/mo with 250, monthly only, annual figure not rendered.",
    },
    {
      date: "2026-10-25",
      text: `Crayo prices updated to the October 25, 2026 ladder: Hobby is ${crayoPlanTerms(hobby)}, with quarterly the default term at checkout. Verdicts and the corrections box re-checked at the new prices; competitor prices unchanged from the October 8 to 9 check.`,
    },
  ],
  relatedSpokes: ["crayo-vs-autoshorts", "crayo-vs-vsub", "crayo-vs-revid"],
  featurePath: "/features/auto-clip",
  ctaHref: "https://crayo.ai/create/auto-clip",
  ctaLabel: "Clip a video now",
  targetQueries: [
    "clippie ai alternative",
    "clippie ai vs crayo",
    "crayo vs clippie ai",
    "clippie ai pricing",
    "clippie ai review",
    "best crayo ai alternative",
  ],
};
